import React from "react"
import {
  AlertTriangle,
  BrainCircuit,
  CheckCircle2,
  Flag,
  Loader2,
  RotateCcw,
} from "lucide-react"
import type { Attempt } from "../types"
import "./LockedAttemptBanner.style.scss"

interface LockedAttemptBannerProps {
  attempt: Attempt
  canStartNew: boolean
  onNewAttempt: () => void
}

const LockedAttemptBanner = ({
  attempt,
  canStartNew,
  onNewAttempt,
}: LockedAttemptBannerProps) => {
  if (attempt.status === "draft") return null

  const config = (() => {
    switch (attempt.status) {
      case "marking":
        return {
          tone: "is-marking",
          icon: <Loader2 size={16} className="is-spinning" />,
          text: "The AI Assessor is currently marking this attempt. Your answer is locked while marking is in progress.",
        }
      case "marked":
        return {
          tone: "is-marked",
          icon: <BrainCircuit size={16} />,
          text: "This attempt has been marked by the AI Assessor. The answer is now read-only.",
        }
      case "submitted":
        return {
          tone: "is-submitted",
          icon: <CheckCircle2 size={16} />,
          text: "This attempt is complete. The answer is now read-only.",
        }
      case "flagged_for_review":
        return {
          tone: "is-flagged",
          icon: <Flag size={16} />,
          text: "This attempt has been flagged for manual review. Score shown is provisional.",
        }
      case "failed":
        return {
          tone: "is-submitted",
          icon: <AlertTriangle size={16} />,
          text:
            attempt.assessmentError ||
            "AI marking could not be completed for this attempt.",
        }
      default:
        return null
    }
  })()

  if (!config) return null

  return (
    <div className={`locked-attempt-banner ${config.tone}`}>
      <div className="locked-attempt-banner__body">
        <span className="locked-attempt-banner__icon">{config.icon}</span>
        <p>{config.text}</p>
      </div>
      {attempt.status !== "marking" && canStartNew && (
        <button type="button" onClick={onNewAttempt}>
          <RotateCcw size={12} /> New Attempt
        </button>
      )}
    </div>
  )
}

export default LockedAttemptBanner
